import * as Yup from 'yup'
import validaCNPJ from 'utils/validaCNPJ'
import { isValid as validateCPF } from 'cpf'
import i18n from 'i18n'

const contactsSchema = (documentType?: string) => {
  return Yup.array().of(
    Yup.object().shape({
      name: Yup.string().required().label(i18n.t('common:name')),
      email: Yup.string().required().email().label(i18n.t('common:email')),
      phone: Yup.string()
        .required()
        .test('phone', i18n.t('inputErrors:invalidPhone'), function (value) {
          // Para empresas internacionais, apenas verifica se o campo não está vazio
          if (documentType === 'internacional') {
            return value && value.length > 0
          }
          // Para empresas nacionais, mantém a validação de máscara brasileira
          return value && value.length >= 13 && value.length <= 14
        })
        .label(i18n.t('common:phone'))
    })
  )
}

export const certificationSchema = () => {
  return Yup.object().shape({
    name: Yup.string().required().label(i18n.t('indCertifications:name')),
    acronym: Yup.string()
      .required()
      .label(i18n.t('indCertifications:acronymous')),
    number: Yup.string().required().label(i18n.t('indCertifications:number')),
    idCertifier: Yup.string()
      .required()
      .label(i18n.t('indCertifications:idCertifier')),
    nameCertifier: Yup.string()
      .required()
      .label(i18n.t('indCertifications:nameCertifier')),
    dtEmitted: Yup.string().nullable().default(null).required(),
    dtExpiry: Yup.string().nullable().default(null).required(),
    emails: Yup.string()
      .required()
      .label(i18n.t('indCertifications:notificationsEmails'))
      .test('is-valid-emails', 'Um ou mais valores são inválidos', value => {
        if (!value) return false
        const emails = value.split(',').map(email => email.trim())
        const emailSchema = Yup.string().email()
        for (const email of emails) {
          if (email === '') return false
          try {
            emailSchema.validateSync(email)
          } catch (err) {
            return false
          }
        }
        return true
      }),
    alert: Yup.string().nullable().default(null).required(),
    file: Yup.object().nullable().default(null).required()
  })
}

export const certificatesSchema = () => {
  return Yup.array().of(
    Yup.object().shape({
      name: Yup.string().required().label(i18n.t('indCertifications:name')),
      acronym: Yup.string()
        .required()
        .label(i18n.t('indCertifications:acronymous')),
      number: Yup.string().required().label(i18n.t('indCertifications:number')),
      idCertifier: Yup.string()
        .required()
        .label(i18n.t('indCertifications:idCertifier')),
      nameCertifier: Yup.string()
        .required()
        .label(i18n.t('indCertifications:nameCertifier')),
      dtEmitted: Yup.string().nullable().default(null).required(),
      dtExpiry: Yup.string().nullable().default(null).required(),
      emails: Yup.string()
        .required()
        .label(i18n.t('indCertifications:notificationsEmails')),
      alert: Yup.string()
        .required()
        .label(i18n.t('indCertifications:timeAlert')),
      file: Yup.object().nullable().default(null).required()
    })
  )
}

export const IndustryFormSchema = () => {
  return Yup.object().shape({
    name: Yup.string().required().label(i18n.t('common:socialReason')),
    fancyName: Yup.string().required().label(i18n.t('common:fantasyName')),
    inspectionType: Yup.string()
      .test(
        'inspectionType',
        i18n.t('inputErrors:isRequired'),
        (value, props) => {
          if (props.parent.documentType === 'nacional') {
            return ['SIF', 'SIE', 'SIM'].includes(value)
          }
          return true
        }
      )
      .label(i18n.t('common:typeInspection')),
    inspectionNum: Yup.string()
      .test(
        'inspectionNum',
        i18n.t('inputErrors:isRequired'),
        (value, props) => {
          if (props.parent.documentType === 'nacional') {
            return value && value.length > 0
          }
          return true
        }
      )
      .label(i18n.t('common:inspectionNumber')),
    cnpj: Yup.string()
      .required()
      .test('cnpj', i18n.t('inputErrors:invalidCNPJ'), (value, props) => {
        if (value && props.parent.documentType === 'nacional') {
          return validaCNPJ(value)
        } else return value.length > 0
      })
      .label(i18n.t('common:CNPJ')),
    ie: Yup.string()
      .nullable(true)
      .test('state', i18n.t('inputErrors:isRequired'), (value, props) => {
        if (props.parent.documentType === 'nacional') return value.length >= 1
        return true
      })
      .label(i18n.t('common:stateRegistration')),
    status: Yup.boolean()
      .nullable(true)
      .required()
      .label(i18n.t('common:status')),
    address: Yup.string()
      .test('address', i18n.t('inputErrors:isRequired'), (value, props) => {
        if (props.parent.documentType === 'nacional') {
          return value && value.length > 0
        }
        return true
      })
      .label(i18n.t('common:address')),
    state: Yup.string()
      .test('state', i18n.t('inputErrors:isRequired'), (value, props) => {
        if (props.parent.documentType === 'nacional') {
          return value && value.length > 0
        }
        return true
      })
      .label(i18n.t('common:state')),
    city: Yup.string()
      .test('city', i18n.t('inputErrors:isRequired'), (value, props) => {
        if (props.parent.documentType === 'nacional') {
          return value && value.length > 0
        }
        return true
      })
      .label(i18n.t('common:city')),
    cep: Yup.string()
      .test('cep', i18n.t('inputErrors:isRequired'), (value, props) => {
        if (props.parent.documentType === 'nacional') {
          return value && value.length >= 10
        }
        return true
      })
      .label(i18n.t('common:CEP')),
    country: Yup.string()
      .test('country', i18n.t('inputErrors:isRequired'), (value, props) => {
        if (props.parent.documentType === 'internacional') {
          return value && value.length > 0
        }
        return true
      })
      .label(i18n.t('common:country')),
    lat: Yup.number()
      .nullable(true)
      .label(i18n.t('common:latitude'))
      .typeError(i18n.t('inputErrors:invalidLatitude')),
    lng: Yup.number()
      .nullable(true)
      .label(i18n.t('common:longitude'))
      .typeError(i18n.t('inputErrors:invalidLongitude')),
    qualifications: Yup.array().min(1).label(i18n.t('common:license')),
    sifPhoto: Yup.mixed()
      .test('sifPhoto', i18n.t('inputErrors:isRequired'), (value, props) => {
        // SIF Photo não é obrigatório para empresas internacionais
        if (props.parent.documentType === 'internacional') {
          return true
        }
        return value != null
      })
      .label(i18n.t('profile:SIFPhoto')),
    contacts: Yup.lazy((val, options) => {
      const documentType = options.parent?.documentType
      return contactsSchema(documentType)
    }),
    technician: Yup.object().shape({
      bio: Yup.string()
        .test('bio', i18n.t('inputErrors:isRequired'), (value, props) => {
          if (props.parent && props.parent.documentType === 'nacional') {
            return value && value.length > 0
          }
          return true
        })
        .label(i18n.t('profile:miniCurriculum')),
      doc: Yup.string()
        .test('doc', i18n.t('inputErrors:isRequired'), (value, props) => {
          if (props.parent && props.parent.documentType === 'nacional') {
            return value && value.length > 0
          }
          return true
        })
        .label(i18n.t('profile:docNumber')),
      name: Yup.string()
        .test('name', i18n.t('inputErrors:isRequired'), (value, props) => {
          if (props.parent && props.parent.documentType === 'nacional') {
            return value && value.length > 0
          }
          return true
        })
        .label(i18n.t('common:name'))
    }),
    manager: Yup.object().shape({
      bio: Yup.string()
        .test('bio', i18n.t('inputErrors:isRequired'), (value, props) => {
          if (props.parent && props.parent.documentType === 'nacional') {
            return value && value.length > 0
          }
          return true
        })
        .label(i18n.t('profile:miniCurriculum')),
      doc: Yup.string()
        .test('doc', i18n.t('inputErrors:isRequired'), (value, props) => {
          if (props.parent && props.parent.documentType === 'nacional') {
            if (!value || value.length === 0) return false
            return validateCPF(value)
          }
          return true
        })
        .label(i18n.t('profile:docNumber')),
      name: Yup.string()
        .test('name', i18n.t('inputErrors:isRequired'), (value, props) => {
          if (props.parent && props.parent.documentType === 'nacional') {
            return value && value.length > 0
          }
          return true
        })
        .label(i18n.t('common:name'))
    }),
    certificates: certificatesSchema(),
    documentType: Yup.string().oneOf(['nacional', 'internacional']).required()
  })
}
